import { z } from "zod";
/**
 * Pagination — the SSOT shapes for offset-based list endpoints.
 *
 * Today each list schema hand-rolls its own `limit`/`offset` fields and
 * most endpoints return a bare `{ items }` (or a bare array) with no
 * total — so the frontend can't render a "page 2 of 9" or a "load more"
 * affordance (assessment U8 / gap 1.5: lists silently truncate at the
 * server cap). These shapes are the foundation the pagination UI needs:
 *
 *   - `PaginationParams` — a reusable base to `.merge()` into a list
 *     query schema instead of re-declaring `limit`/`offset` each time.
 *   - `paginated(item)` — wraps an item schema into the standard
 *     envelope `{ items, total, limit, offset, hasMore }`. New and
 *     migrated list endpoints return this; the API builds it with
 *     `buildPage()` (packages/api/src/lib/pagination.ts).
 *
 * `hasMore` is computed server-side (`offset + items.length < total`) so
 * the client never has to reconstruct it.
 */
/** Reusable offset/limit params. Merge into a domain list query:
 *  `ContactListQuerySchema = z.object({ ... }).merge(PaginationParams)`.
 *  Defaults match the existing house convention (limit 50, offset 0). */
export const PaginationParams = z.object({
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
/** The metadata half of a paginated response, without the items. */
export const PageMetaSchema = z.object({
    total: z.number().int().nonnegative(),
    limit: z.number().int().positive(),
    offset: z.number().int().nonnegative(),
    hasMore: z.boolean(),
});
/** Wrap an item schema into the standard `{ items, ...meta }` envelope. */
export function paginated(item) {
    return PageMetaSchema.extend({ items: z.array(item) });
}
//# sourceMappingURL=pagination.js.map